import React, { useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import globalStyle from "../../styles/globalStyle";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  closeOnBackdropClick?: boolean;
  showCloseButton?: boolean;
  className?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  closeOnBackdropClick = true,
  showCloseButton = true,
  className = "",
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-full mx-4",
  }[size];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-70 transition-opacity"
        aria-hidden="true"
        onClick={closeOnBackdropClick ? onClose : undefined}
      />

      {/* Modal Container */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className={`${sizeClasses} w-full transform overflow-hidden rounded-2xl bg-gray-900 ${
            globalStyle.shadowLg
          } transition-all duration-300 ${
            isOpen
              ? "animate-fade-in scale-100 opacity-100"
              : "scale-95 opacity-0"
          } ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div
              className={`flex items-center justify-between px-6 py-4 border-b ${globalStyle.border}`}
            >
              {title && <h3 className={globalStyle.h5}>{title}</h3>}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="ml-4 rounded-lg p-2 hover:bg-gray-800 transition-colors duration-200"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5 text-white" />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="px-6 py-4 bg-black">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
